import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, StatCard, Surface, Empty } from "../components/Primitives";
import { Plus, Trash2, ArrowDown, ArrowUp, Banknote, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Cash() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "user";
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [summary, setSummary] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ type: "recette", amount: 0, label: "", category: "", note: "" });

  const load = async () => {
    const { data } = await api.get(`/cash/summary?date=${date}`);
    setSummary(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cash", { ...form, amount: Number(form.amount), date });
      toast.success(`${form.type === "recette" ? "Recette" : "Dépense"} enregistrée`);
      setShow(false); setForm({ type: "recette", amount: 0, label: "", category: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    try { await api.delete(`/cash/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!summary) return <div className="text-[var(--text-muted)] font-data text-sm">Chargement...</div>;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Caisse journalière"
        subtitle="Recettes · Dépenses · Solde"
        actions={
          <>
            <input type="date" data-testid="cash-date" value={date} onChange={(e) => setDate(e.target.value)} className="input-base w-44"/>
            {canEdit && <button data-testid="new-cash-btn" className="btn-primary flex items-center gap-2" onClick={() => setShow(!show)}><Plus size={14}/>Nouveau mouvement</button>}
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard label="Ventes du jour" value={`${summary.ventes_total.toLocaleString("fr-FR")} F`} accent="cyan" sub={`${summary.sales_count} vente(s)`} icon={Banknote}/>
        <StatCard label="Encaissé ventes" value={`${summary.ventes_encaissees.toLocaleString("fr-FR")} F`} accent="green"/>
        <StatCard label="Encaiss. commandes" value={`${(summary.encaissements_commandes||0).toLocaleString("fr-FR")} F`} accent="green" sub={`${summary.order_payments_count||0} paiement(s)`} icon={ArrowDown}/>
        <StatCard label="Recettes diverses" value={`${summary.recettes_diverses.toLocaleString("fr-FR")} F`} accent="green" icon={ArrowDown}/>
        <StatCard label="Dépenses" value={`${summary.depenses.toLocaleString("fr-FR")} F`} accent="pink" icon={ArrowUp}/>
        <StatCard label="Solde caisse" value={`${summary.solde_caisse.toLocaleString("fr-FR")} F`} accent="cyan" icon={Wallet}/>
      </div>

      {show && canEdit && (
        <Surface title="Nouveau mouvement de caisse">
          <form onSubmit={submit} className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="label-mono block mb-1.5">Type</label>
              <select data-testid="cash-type" className="input-base" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="recette">Recette</option>
                <option value="depense">Dépense</option>
              </select>
            </div>
            <div><label className="label-mono block mb-1.5">Libellé</label><input data-testid="cash-label" required className="input-base" placeholder="Achat sachets, frais transport..." value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}/></div>
            <div><label className="label-mono block mb-1.5">Montant (F)</label><input data-testid="cash-amount" required type="number" min="0" step="1" className="input-base" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}/></div>
            <div><label className="label-mono block mb-1.5">Catégorie</label><input className="input-base" placeholder="ex: fourniture, salaire" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}/></div>
            <div className="col-span-full"><label className="label-mono block mb-1.5">Note</label><input className="input-base" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}/></div>
            <div className="col-span-full flex gap-2"><button data-testid="submit-cash" type="submit" className="btn-primary">Enregistrer</button><button type="button" className="btn-ghost" onClick={() => setShow(false)}>Annuler</button></div>
          </form>
        </Surface>
      )}

      <Surface title={`Mouvements du ${date}`}>
        {summary.movements.length === 0 ? <Empty message="Aucun mouvement"/> : (
          <table className="data-table">
            <thead><tr><th>Heure</th><th>Type</th><th>Libellé</th><th>Catégorie</th><th>Montant</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {summary.movements.map((m) => (
                <tr key={m.id}>
                  <td className="font-data text-[var(--text-muted)] text-xs">{m.created_at?.slice(11, 16)}</td>
                  <td>{m.type === "recette" ? <span className="badge-success flex items-center gap-1 w-fit"><ArrowDown size={10}/>Recette</span> : <span className="badge-alert flex items-center gap-1 w-fit"><ArrowUp size={10}/>Dépense</span>}</td>
                  <td>{m.label}</td>
                  <td className="text-[var(--text-muted)] text-xs">{m.category || "—"}</td>
                  <td className={`font-data ${m.type === "recette" ? "neon-text-green" : "neon-text-pink"}`}>{m.amount.toLocaleString("fr-FR")} F</td>
                  {canEdit && <td><button onClick={() => remove(m.id)} className="text-[var(--text-muted)] hover:text-[var(--neon-pink)]"><Trash2 size={14}/></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Surface>

      <Surface title={`Encaissements de commandes — ${date}`}>
        {(!summary.order_payments || summary.order_payments.length === 0) ? <Empty message="Aucun encaissement de commande aujourd'hui"/> : (
          <table className="data-table" data-testid="order-payments-table">
            <thead><tr><th>Heure</th><th>Commande / Facture</th><th>Client</th><th>Mode</th><th>Encaissé par</th><th>Note</th><th className="text-right">Montant</th></tr></thead>
            <tbody>
              {summary.order_payments.map((p) => (
                <tr key={p.id}>
                  <td className="font-data text-[var(--text-muted)] text-xs">{p.created_at?.slice(11, 16)}</td>
                  <td className="font-data font-semibold text-[var(--primary-dark,#8E3A6D)]">{p.invoice_number || p.order_number || "—"}</td>
                  <td>{p.customer_name || <span className="text-[var(--text-muted)]">—</span>}</td>
                  <td className="text-xs capitalize">{p.method?.replace("_"," ") || "—"}</td>
                  <td className="text-xs text-[var(--text-muted)]">{p.by_driver_name ? `🚚 ${p.by_driver_name}` : "Admin"}</td>
                  <td className="text-xs text-[var(--text-muted)] italic">{p.note || ""}</td>
                  <td className="font-data text-right neon-text-green font-bold">{Math.round(p.amount).toLocaleString("fr-FR")} F</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Surface>
    </div>
  );
}
